import { Chat } from "./Chat";
import { ReactComponent as IconRegenerate } from "../../../images/icon-regenerate.svg";
import { ReactComponent as IconCopy } from "../../../images/icon-copy (1).svg";
import { ReactComponent as IconLike } from "../../../images/icon-like.svg";
import { ReactComponent as IconDislike } from "../../../images/icon-dislike.svg";
import { ReactComponent as IconEmail } from "../../../images/icon-write-email.svg";
import { ReactComponent as IconDraft } from "../../../images/icon-draft.svg";
import { ReactComponent as IconReview } from "../../../images/icon-review.svg";
import { ReactComponent as IconChatError } from "../../../images/icon-chat-error.svg";
import { ReactComponent as IconSimplify } from "../../../images/icon-simplify.svg";
import { ReactComponent as IconTargetArrow } from "../../../images/icon-target-arrow.svg";
import { ReactComponent as IconSweatshirt } from "../../../images/icon-sweatshirt.svg";
import { ReactComponent as IconJustify } from "../../../images/icon-justify-paragraph.svg";
import { ReactComponent as IconCreativity } from "../../../images/icon-creativity.svg";
import { ReactComponent as IconBriefcase } from "../../../images/icon-briefcase.svg";
import { ReactComponent as IconSubject } from "../../../images/icon-subject.svg";

import myCaiApiService from "../../../services/mycai-api-v1";
import { useAuthentication } from "../../../contexts/AuthContext";
import { useMessages } from "../contexts/MessageContext";
import { useAccess } from "../contexts/AccessContext";
import { useProfilePictures } from "../contexts/ProfilePictureContext";
import { Avatar } from "cai-fusion";
import { useTransientVisibility } from "../../fusion/Contexts/VisualContext";
import { copyMarkdownToClipboard } from "../../fusion/Utilities/CopyContent";
import { debugLog } from "../../../configs/loggingConfig";
import env from "../../../configs/envConfig";

const avatarAI = require("../../../images/avatar-ai-classic.png");

const SymphonyChat = ({ lockInput }) => {
	let { graphData, impersonating, apiAccessToken } = useAuthentication();
	let { userProfileImages } = useProfilePictures();
	const { addTemporaryAssistantMessage, setEditingMessageId, getAuditLog } = useMessages();
	const { canSendMessage } = useAccess();
	const { regenerateMessage } = useMessages();	
	const { show } = useTransientVisibility();

	// This will create a list of actions that are to be removed.
	// Emphasis is on *disabling* instead of *enabling*.
	let removedActions = env.REACT_APP_ALLEGRO_HIDE_ACTIONS === "" ? [] : (env.REACT_APP_ALLEGRO_HIDE_ACTIONS ?? "").split(',')

	return (
		<Chat
			lockInput={lockInput}
			// let the Application define the actions, useful for reusing Chat across types of apps
			chatEntryActions={{
				user: [
					{
						icon: IconDraft,
						alt: "Edit Message",
						onClick: (messageId) => {setEditingMessageId(messageId)},
					},
					canSendMessage && {
						icon: IconCopy,
						alt: "Copy Message",
						onClick: (_, __, text) => {copyMarkdownToClipboard(text)}, 
					}
				],
				assistant: [
					(canSendMessage && !removedActions.includes("REGENERATE")) && {
						icon: IconRegenerate,
						alt: "Regenerate Message",
						options: [
							{
								text: "Shorter",
								Icon: IconSubject,
								actionFunction: (messageId) => regenerateMessage(messageId, "Shorter"),
							},
							{
								text: "Longer",
								Icon: IconJustify,
								actionFunction: (messageId) => regenerateMessage(messageId, "Longer"),
							},
							{
								text: "Simpler",
								Icon: IconSimplify,
								actionFunction: (messageId) => regenerateMessage(messageId, "Simpler"),
							},
							{
								text: "More Casual",
								Icon: IconSweatshirt,
								actionFunction: (messageId) => regenerateMessage(messageId, "More Casual"),
							},
							{
								text: "More Professional",
								Icon: IconBriefcase,
								actionFunction: (messageId) => regenerateMessage(messageId, "More Professional"),
							},
							{
								text: "More Creative",
								Icon: IconCreativity,
								actionFunction: (messageId) => regenerateMessage(messageId, "More Creative"),
							},
							{
								text: "More Focused",
								Icon: IconTargetArrow,
								actionFunction: (messageId) => regenerateMessage(messageId, "More Focused"),
							},
							{
								text: "Just Try Again",
								Icon: IconRegenerate,
								actionFunction: (messageId) => regenerateMessage(messageId, "Just Try Again"),
							},
						],
					},
					!removedActions.includes("COPY") && {
						icon: IconCopy,
						alt: "Copy Message",
						onClick: (_, __, text) => {
							copyMarkdownToClipboard(text);
							show('PlagiarismSnackbar');
						},
					},
					(canSendMessage && !removedActions.includes("DISLIKE")) && {
						icon: IconDislike,
						alt: "Dislike Message",
						onClick: (_, messagePairId) => {
							myCaiApiService.SymphonyApiService.RateMessage(apiAccessToken, impersonating, messagePairId, false) // false for dislike
								.then((response) => {
									debugLog.log(response);
									// update UI or state based on response
									// TODO, make an API call with the assistant instruction only if the user dislikes the message
									// this will tune the response to the assistant, and possibly have more context
									addTemporaryAssistantMessage("I'm really sorry to hear that my response didn't meet your expectations. I'm here to assist you in the best way possible. Could you please clarify or provide more details on how I can assist you better? Your feedback will greatly help me in improving our interaction.");
								})
								.catch((error) => { debugLog.error(error); });
						},
					},
					(canSendMessage && !removedActions.includes("AUDIT")) && {
						icon: IconReview,
						alt: "View Audit Trail",
						onClick: async (messageId) => {
							getAuditLog(messageId);
							show('AuditLogDrawer');
						},
					}
				],
				error: [
					canSendMessage && {
						icon: IconEmail,
						alt: "Email Error",
						onClick: (messageId, messagePairId) => {
							const bugReportLink = `mailto:Christopher.Zumberge@cai.io?cc=eae5321e.cai.io@amer.teams.ms&subject=Error%20Report%20from%20Maestro%20Chat&body=Please%20feel%20free%20to%20add%20any%20additional%20details%20or%20notes%20here%3B%0D%0A%0D%0AReported%20On%3A%20${new Date().toLocaleString(
								"en-US",
								{
									month: "short",
									day: "numeric",
									year: "numeric",
									hour: "numeric",
									minute: "numeric",
								}
							)}%0D%0AReported%20By%3A%20${
								graphData.displayName
							}%0D%0A%0D%0AMessageId%3A%20${messageId}%0D%0AMessagePairId%3A%20${messagePairId}%0D%0A`;
							window.location.href = bugReportLink;
						},
					},
					canSendMessage && {
						icon: IconRegenerate,
						alt: "Regenerate Message",
						onClick: (messageId) => {regenerateMessage(messageId)},
					}
				],
				info: [],
			}}
			iconElements={{
				user: (_, UserId) => (<Avatar src={userProfileImages[UserId] ?? null} />),
				assistant: () => (<img src={avatarAI} className="a-avatar__img a-avatar__img--square" />),
				error: () => (<IconChatError className="a-avatar__img a-avatar__img--square" />)
			}}
		/>
	);
};
export default SymphonyChat;
